/*******************************************************
* Form Text Field
* 
* A reusable form text field component that wraps MUI TextField with form-specific
* functionality including error handling and value management.
* 
* Key Features:
* - Integrated form field error handling
* - Standardized onChange handling
* - MUI TextField integration with consistent styling
********************************************************/
'use client';
//########## IMPORTS ##########
// #region IMPORTS
import { TextField, type TextFieldProps } from '@mui/material';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
interface FormTextFieldProps extends Omit<TextFieldProps, 'onChange' | 'error'> {
    name: string;
    onChange?: (field: string, value: string) => void;
    error?: string;
    clearError?: () => void;
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS

//#region CONSTANT - STYLES
const inputLabelStyles = { color: 'text.primary' };
//#endregion
//#endregion


//########## FUNCTIONS ##########
// #region FUNCTIONS
export const FormTextField: React.FC<FormTextFieldProps> = ({
    name,
    value,
    onChange,
    error,
    clearError,
    required = false,
    fullWidth = true,
    margin = "dense",
    label,
    type,
    ...rest
}) => {
    //########## MAIN LOGIC ##########
    // #region MAIN LOGIC
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(name, event.target.value);
        clearError?.();
    };
    //#endregion

    //########## MARKDOWN ##########
    // #region MARKDOWN
    return (
        <TextField
            id={name}
            name={name}
            value={value}
            onChange={handleChange}
            required={required}
            fullWidth={fullWidth}
            margin={margin}
            label={label}
            type={type}
            error={!!error}
            helperText={error}
            slotProps={{
                inputLabel: {
                    sx: {
                        color: value ? inputLabelStyles : 'text.disabled',
                        '&.Mui-focused': { color: inputLabelStyles }
                    }
                }
            }}
            {...rest}
        />
    );
    //#endregion
};
